import { RowFieldErrors, TenantsValidationError, TenantRow } from '../types';

const hasOnlyEmptyRows = (rows: TenantRow[]) => {
  const filledRows = rows.filter(({ key, value }) => key.trim() || value.trim());
  return rows.length > 0 && filledRows.length === 0;
};

const markEmptyDraftRowErrors = (rows: TenantRow[], errors: Record<string, RowFieldErrors>) => {
  if (!hasOnlyEmptyRows(rows)) {
    return;
  }

  for (const row of rows) {
    errors[row.id] = { key: true, value: true };
  }
};

const markRequiredFieldErrors = (rows: TenantRow[], errors: Record<string, RowFieldErrors>) => {
  for (const row of rows) {
    const key = row.key.trim();
    const value = row.value.trim();

    if (!key && !value) {
      continue;
    }

    if (!key) {
      errors[row.id] = { ...errors[row.id], key: true };
    }

    if (key && !value) {
      errors[row.id] = { ...errors[row.id], value: true };
    }
  }
};

const markDuplicateKeyErrors = (rows: TenantRow[], errors: Record<string, RowFieldErrors>) => {
  const keyToRowIds = new Map<string, string[]>();

  for (const row of rows) {
    const key = row.key.trim();

    if (!key) {
      continue;
    }

    const rowIds = keyToRowIds.get(key) ?? [];
    rowIds.push(row.id);
    keyToRowIds.set(key, rowIds);
  }

  keyToRowIds.forEach((rowIds) => {
    if (rowIds.length < 2) {
      return;
    }

    for (const rowId of rowIds) {
      errors[rowId] = { ...errors[rowId], key: true };
    }
  });
};

export const getRowFieldErrors = (rows: TenantRow[]): Record<string, RowFieldErrors> => {
  const errors: Record<string, RowFieldErrors> = {};

  markEmptyDraftRowErrors(rows, errors);
  markRequiredFieldErrors(rows, errors);
  markDuplicateKeyErrors(rows, errors);

  return errors;
};

export const validateRows = (rows: TenantRow[]): TenantsValidationError | null => {
  if (hasOnlyEmptyRows(rows)) {
    return 'emptyKey';
  }

  for (const row of rows) {
    const key = row.key.trim();
    const value = row.value.trim();

    if (!key && !value) {
      continue;
    }

    if (key && !value) {
      return 'emptyValue';
    }

    if (!key && value) {
      return 'emptyKey';
    }
  }

  const keys = rows.map(({ key }) => key.trim()).filter(Boolean);

  if (new Set(keys).size !== keys.length) {
    return 'duplicateKey';
  }

  return null;
};
